import { DateTime } from 'luxon';
import type { Locales, TranslationFunctions } from '@/i18n/i18n-types';
import { getLuxonLocale } from '@/lib/utils';
import type {
  TenantControllerGetTenantSalesCsvData,
  TenantControllerGetTenantSalesData,
} from '@/generated/korza-api-client/types.gen';

/** Sales history page sizes: desktop table (as Vendas today) and mobile "Carregar mais". */
export const SALES_DESKTOP_PAGE_SIZE = 7;
export const SALES_MOBILE_PAGE_SIZE = 20;

export interface SalesHistoryFilterValues {
  fromDate: string;
  toDate: string;
  textSearch: string;
  paymentStatus: string[];
  campaignIds: string[];
}

type SalesQuery = Omit<
  NonNullable<TenantControllerGetTenantSalesData['query']>,
  'page' | 'limit'
>;

const joinOrUndefined = (values: string[]) =>
  values.length ? values.join(',') : undefined;

/** Filters of `GET /tenants/:slug/sales` (same rules as `useSales`). */
export function buildSalesQuery(v: SalesHistoryFilterValues): SalesQuery {
  return {
    fromDate: v.fromDate,
    toDate: v.toDate,
    textSearch: v.textSearch.trim() || undefined,
    paymentStatus: joinOrUndefined(v.paymentStatus),
    campaignIds: joinOrUndefined(v.campaignIds),
  };
}

export function buildSalesCsvQuery(
  v: SalesHistoryFilterValues,
  locale: Locales
): NonNullable<TenantControllerGetTenantSalesCsvData['query']> {
  return { ...buildSalesQuery(v), locale: locale === 'pt' ? 'pt' : 'en' };
}

/** `getNextPageParam` of the mobile list. */
export function getNextSalesPage(last: {
  page: number;
  totalPages: number;
}): number | undefined {
  return last.page < last.totalPages ? last.page + 1 : undefined;
}

/**
 * All loaded sales in page order. A sale can show up again on a later page
 * when new ones arrive between requests (offset shifts); the first one stays.
 */
export function flattenSalesPages<T extends { id: string }>(
  pages: ReadonlyArray<{ data: T[] }>
): T[] {
  const seen = new Set<string>();
  return pages.flatMap((page) =>
    page.data.filter((sale) => {
      if (seen.has(sale.id)) return false;
      seen.add(sale.id);
      return true;
    })
  );
}

/**
 * Filters of the mobile "Filtros avançados" sheet that differ from the
 * defaults: 1 when not both statuses are selected, plus each campaign.
 */
export function countAdvancedSalesFilters(
  v: Pick<SalesHistoryFilterValues, 'paymentStatus' | 'campaignIds'>
): number {
  return (v.paymentStatus.length === 2 ? 0 : 1) + v.campaignIds.length;
}

/** "Camiseta + 2": the first product and how many others. */
export function firstProductLabel(
  names: string[]
): { first: string; extra: number } | null {
  return names.length ? { first: names[0], extra: names.length - 1 } : null;
}

/** Date and time as in the Vendas table (`DATETIME_SHORT`). */
export const formatSaleDateTime = (iso: string, locale: Locales) =>
  DateTime.fromISO(iso)
    .setLocale(getLuxonLocale(locale))
    .toLocaleString(DateTime.DATETIME_SHORT);

export const formatSaleAmount = (
  sale: { amount: number; currency: string },
  locale: Locales
) =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: sale.currency,
  }).format(sale.amount);

export const formatSaleStatus = (status: string, LL: TranslationFunctions) => {
  switch (status) {
    case 'paid':
      return LL.salesPage.filters.paid();
    case 'unpaid':
      return LL.salesPage.filters.unpaid();
    default:
      return status;
  }
};
